export interface Tomo {
  /** Número visual de la tarjeta: '01' … '06'. */
  num: string
  /** Nombre real del tomo, tal como aparece en el mockup oficial. */
  title: string
  /** Beneficio concreto en una línea. */
  outcome: string
  /** 2 o 3 cosas concretas que la persona puede implementar. */
  points: string[]
}
export interface Pillar {
  title: string
  desc: string
}
export interface Bono {
  num: string
  /** Etiqueta editable. Reemplazar por el nombre definitivo cuando esté disponible. */
  label: string
  /** Portada del bono. Cuando exista, se muestra en lugar del marco vacío. */
  cover?: string
}
export interface Benefit {
  title: string
  desc: string
}
export interface Faq {
  q: string
  a: string
}

/** Precio único vigente de la landing. */
/**
 * Formato monetario único de la landing: siempre "AR$ 00.000".
 * Nunca añadir "ARS" cuando ya se antepone "AR$".
 */
export const PRICE = {
  previous: 'AR$ 49.990',
  current: 'AR$ 19.990',
  note: 'Pago único',
} as const

export const PRODUCT_FACTS: string[] = [
  'Mega Pack 6 en 1',
  '5 Bonos de regalo',
  '100% digital',
  'Acceso inmediato',
]

/** Los seis tomos del Mega Pack — nombres del mockup oficial. */
export const TOMOS: Tomo[] = [
  {
    num: '01',
    title: 'Arquitectura de Autoridad',
    outcome:
      'Que quien entre a tu perfil entienda en segundos quién sos, a quién ayudás y por qué elegirte.',
    points: [
      'Ordenar tu perfil, tu bio y lo que mostrás primero',
      'Definir tu especialidad para dejar de ser "uno más"',
      'Mostrar tu experiencia sin sonar arrogante',
    ],
  },
  {
    num: '02',
    title: 'Ingeniería de Oferta Irresistible',
    outcome:
      'Presentar tus sesiones, programas o acompañamientos de una forma que la gente entienda y quiera.',
    points: [
      'Armar tu propuesta para que se entienda en una frase',
      'Explicar el valor de tu proceso sin justificar el precio',
      'Responder consultas sin sentir que perseguís a nadie',
    ],
  },
  {
    num: '03',
    title: 'Contenido de Alto Rendimiento',
    outcome: 'Dejar de abrir Instagram pensando "¿qué publico hoy?".',
    points: [
      'Saber qué publicar y con qué intención detrás',
      'Tipos de contenido que atraen, conectan y venden',
      'Ideas para no volver a improvisar una publicación',
    ],
  },
  {
    num: '04',
    title: 'Reels & Video Authority',
    outcome: 'Llegar a personas nuevas y comunicar con confianza frente a la cámara.',
    points: [
      'Estructuras de video que retienen y suman alcance',
      'Grabar con el celular sin equipos caros',
      'Perder el miedo a mostrarte y hablar con criterio',
    ],
  },
  {
    num: '05',
    title: 'El Sistema de Captación',
    outcome: 'Convertir seguidores y consultas en conversaciones reales con potenciales clientes.',
    points: [
      'Guiar a tu audiencia hacia una consulta concreta',
      'Ordenar cómo respondés mensajes y consultas',
      'Dejar de depender solo del boca en boca',
    ],
  },
  {
    num: '06',
    title: 'El Calendario Estratégico PRO',
    outcome: 'Tener resuelto qué comunicar cada semana sin vivir pegado al celular.',
    points: [
      'Planificar tu mes de contenido en una sola sentada',
      'Combinar contenido de valor, autoridad y venta',
      'Sostener presencia sin que te coma el día',
    ],
  },
]

/** Las cinco funciones que cumple el sistema dentro de tu presencia digital. */
export const SYSTEM_PILLARS: Pillar[] = [
  { title: 'Posicionamiento', desc: 'Que se entienda a quién ayudás y con qué resultado.' },
  { title: 'Contenido', desc: 'Qué comunicar cada semana y con qué objetivo detrás.' },
  { title: 'Autoridad', desc: 'Mostrar tu criterio profesional, no solo tu actividad diaria.' },
  { title: 'Conversión', desc: 'Contenido pensado para abrir conversaciones, no solo likes.' },
  { title: 'Oferta', desc: 'Presentar tus sesiones, programas o procesos con claridad.' },
]

export const TOMO_FEATURES: string[] = [
  'Explicado en lenguaje simple, sin jerga de marketing',
  'Ejemplos aplicados al coaching y al acompañamiento',
  'Ejercicios prácticos para completar',
  'Checklists de aplicación',
  'Plantillas y estructuras listas para usar',
  'Sirve para cualquier especialidad',
  'Se lee desde el celular, la tablet o la computadora',
  'Todo orientado a conseguir consultas y clientes',
]

/**
 * Los cinco bonos de regalo. Los nombres definitivos y las portadas se cargan
 * aquí (campo `cover`) en cuanto estén disponibles, sin tocar el componente.
 */
export const BONOS: Bono[] = [
  { num: '01', label: 'Bono 1' },
  { num: '02', label: 'Bono 2' },
  { num: '03', label: 'Bono 3' },
  { num: '04', label: 'Bono 4' },
  { num: '05', label: 'Bono 5' },
]

/** Situaciones cotidianas en las que el coach tiene que reconocerse. */
export const PAIN_BLOCKS: string[] = [
  'Sabés mucho de lo tuyo, pero cuando llega el momento de mostrarlo en redes no sabés por dónde empezar.',
  'Te sentás a pensar qué publicar y terminás improvisando algo para no dejar Instagram abandonado.',
  'Publicás, recibís algunos likes… pero las consultas no llegan.',
  'Sentís que tu perfil no muestra ni la mitad del valor que realmente podés aportar.',
  'Dependés demasiado del boca en boca y de que alguien te recomiende.',
  'Tenés experiencia, formación y resultados, pero otros profesionales parecen tener mucha más presencia online.',
  'Querés conseguir clientes desde tus redes, pero no querés convertirte en experto en marketing para lograrlo.',
]

export const BENEFITS: Benefit[] = [
  {
    title: 'Construí autoridad',
    desc: 'Que cuando alguien llegue a tu perfil entienda rápidamente quién sos, qué hacés y por qué debería elegirte.',
  },
  {
    title: 'Creá contenido con propósito',
    desc: 'Dejá de abrir Instagram pensando "¿qué publico hoy?" y empezá a crear contenido con una intención clara.',
  },
  {
    title: 'Transformá seguidores en oportunidades',
    desc: 'No se trata solamente de sumar likes. Aprendé a construir una presencia que genere conversaciones y potenciales clientes.',
  },
  {
    title: 'Vendé tu acompañamiento',
    desc: 'Aprendé a comunicar tus sesiones, programas, mentorías o procesos sin sentir que estás persiguiendo a la gente para vender.',
  },
  {
    title: 'Ordená tu estrategia',
    desc: 'Tené claro qué comunicar, qué publicar y qué función cumple cada contenido dentro de tu negocio.',
  },
  {
    title: 'Construí una marca profesional',
    desc: 'Hacé que lo que las personas ven online esté a la altura del profesional que sos fuera de las redes.',
  },
]

export const AUDIENCE_POINTS: string[] = [
  'Sos coach o profesional y querés conseguir más clientes utilizando tu presencia digital.',
  'Sabés mucho de tu especialidad pero sentís que eso no se refleja en tus redes.',
  'Publicás contenido pero muchas veces no sabés si realmente sirve para algo.',
  'Dependés demasiado de recomendaciones o del boca en boca.',
  'Vendés sesiones, programas, mentorías o acompañamientos.',
  'Querés construir una marca personal profesional y convertirte en referente de tu especialidad.',
  'Querés generar más consultas sin pasar todo el día creando contenido.',
  'Buscás un sistema práctico que puedas aplicar sin convertirte en especialista en marketing.',
]

export const DISCIPLINES: string[] = [
  'Fitness',
  'Hábitos',
  'Rendimiento',
  'Respiración',
  'Meditación',
  'Bienestar',
  'Desarrollo Personal',
  'Life Coaching',
]

export const TRANSFORM_BEFORE: string[] = [
  'Improvisás contenido',
  'No sabés qué publicar',
  'Tu propuesta no se entiende',
  'Dependés de recomendaciones',
  'Recibís pocas consultas',
  'Tu conocimiento no se percibe',
]

export const TRANSFORM_AFTER: string[] = [
  'Tenés una estrategia clara',
  'Sabés qué comunicar',
  'Construís autoridad',
  'Mostrás profesionalmente tu propuesta',
  'Generás oportunidades de venta',
  'Utilizás tu conocimiento como un activo comercial',
]

export const OFFER_INCLUDES: string[] = [
  '6 tomos del Mega Pack',
  '5 bonos de regalo',
  'Acceso inmediato',
  'Material 100% digital',
  'Aplicación práctica',
  'Acceso desde cualquier dispositivo',
  'Pago único, sin suscripciones',
]

export const FAQS: Faq[] = [
  {
    q: '¿Necesito saber de marketing?',
    a: 'No. Coach Digital PRO está pensado justamente para profesionales cuya especialidad es otra. El sistema te guía paso a paso para que puedas aplicarlo sin convertirte en especialista en marketing digital.',
  },
  {
    q: '¿Es un curso?',
    a: 'No. Es un Mega Pack digital: 6 tomos en formato guía, más 5 bonos con recursos y herramientas para aplicar. No hay clases en vivo ni horarios: lo leés y lo implementás a tu ritmo, desde el celular o la computadora.',
  },
  {
    q: '¿Sirve para cualquier tipo de coach?',
    a: 'Está pensado para profesionales que venden conocimiento, transformación o acompañamiento: fitness, hábitos, rendimiento, respiración, meditación, bienestar, desarrollo personal, life coaching y disciplinas similares.',
  },
  {
    q: '¿Y si ya tengo seguidores?',
    a: 'Perfecto. No necesitás empezar desde cero. El objetivo es ayudarte a aprovechar mejor la audiencia que ya tenés y construir una presencia más estratégica.',
  },
  {
    q: '¿Cómo recibo el material?',
    a: 'Después de realizar la compra recibís acceso al material digital para comenzar a utilizarlo inmediatamente.',
  },
  {
    q: '¿Tengo que aplicar todo de una vez?',
    a: 'No. Podés avanzar paso a paso y utilizar primero las herramientas que más necesites según el momento de tu negocio.',
  },
]
